// Design System
import { UnderlinedButton } from 'jl-design-system/elements/button/Button';
import Link from 'jl-design-system/elements/link/Link';
import reload from 'jl-design-system/utils/reload/reload';

// Config
import errorCodeConstants from '../../constants/errorCodeConstants';
import MyAccountLink from '../../components/my-account-link';
import routeConstants from '../../constants/routeConstants';
import urlConstants from '../../constants/urlConstants';

export const ReturnToBasketLink = () => <Link to={urlConstants.BASKET_PAGE_URL}>return to basket</Link>;

export const ReturnToHomepageLink = () => <Link to={urlConstants.HOME_PAGE_URL}>return to the homepage</Link>;

const errorMessageBodyLookup = ({ bodyLookup, body = '' }) => {
  switch (bodyLookup) {
    case errorCodeConstants.CLIENT_CONNECTIVITY_ERROR_CLICK_COLLECT: {
      return (
        <>
          <p>
            Please try again. If the problem persists, please choose <Link to={routeConstants.DELIVERY}>delivery</Link>{' '}
            or try again later
          </p>
        </>
      );
    }

    case errorCodeConstants.CLIENT_DELIVERY_METHODS_ERROR: {
      return (
        <>
          <p>
            The item(s) in your order are either out of stock from our suppliers or not currently
            available for your chosen delivery address.
          </p>
          <p>Please <ReturnToHomepageLink /> and try another item or change your delivery address.</p>
        </>
      );
    }

    case errorCodeConstants.CARD_PAYMENT_SOFT_DECLINED_DO_NOT_RETRY:
    case errorCodeConstants.CARD_PAYMENT_NOT_AUTHORISED:
      return (
        <>
          <p>Please check your card details and try again.</p>
          <p>If the problem persists, try another card or payment method.</p>
        </>
      );

    case errorCodeConstants.SERVER_INTERNAL_ERROR:
    case errorCodeConstants.BFF_SERVER_INTERNAL_ERROR:
    case errorCodeConstants.CUSTOMER_SYNC_FAILED:
    case errorCodeConstants.BFF_UNKNOWN_API_ERROR: {
      return <>Please <ReturnToBasketLink /> and try again.</>;
    }

    case errorCodeConstants.CARD_PAYMENT_ALREADY_EXISTS: {
      return (
        <>
          <span>
            To resolve the issue, please return to the home page and sign out of your account.
            You&apos;ll need to add your items back to your basket, but then can proceed to checkout as normal.
          </span>
          <span>
            We apologise for any inconvenience caused.
          </span>
          <div><span><ReturnToHomepageLink /></span></div>
        </>
      );
    }

    case errorCodeConstants.CLIENT_PAYMENT_3DS_CANCELLED_ERROR:
    case errorCodeConstants.CLIENT_PAYMENT_OCP_3DS_INVALID_ERROR: {
      return (
        <>
          <p>
            Sorry, we couldn&apos;t place your order because you didn&apos;t complete your bank&apos;s security check.
            No payment has been taken.
          </p>
          <p>
            Please try again, or contact your bank for help with your security code or banking app.
            If the problem persists, please try another payment method.
          </p>
        </>
      );
    }

    case errorCodeConstants.CLIENT_CREATE_ACCOUNT_DETAILS_NOT_SAVED: {
      return (
        <>
          <p>We had some technical problems while creating your account.</p>
          <p>
            Your order has been saved and linked to your account however we&apos;ve been unable
            to save your personal details for future orders. This includes your name, address,
            contact number and any payment information.
          </p>
          <p>
            So when you place an order next you you&apos;ll need to enter this information again.
            Alternatively, you can update your details in <MyAccountLink />.
          </p>
          <p>We apologise for any inconvenience caused.</p>
        </>
      );
    }

    case errorCodeConstants.CLIENT_APPS_CREATE_ACCOUNT_DETAILS_NOT_SAVED: {
      return (
        <>
          <p>We had some technical problems while creating your account.</p>
          <p>
            Your order has been saved and linked to your account however we&apos;ve been unable
            to save your personal details for future orders. This includes your name, address, contact
            number and any payment information.
          </p>
          <p>
            So when you place an order next you you&apos;ll need to enter this information again.
            Alternatively, you can update your details in My Account.
          </p>
          <p>We apologise for any inconvenience caused.</p>
        </>
      );
    }

    case errorCodeConstants.CLIENT_CREATE_ACCOUNT_DETAILS_NOT_SAVED_ORDER_NOT_LINKED: {
      return (
        <>
          <p>
            We had some technical issues linking your order to your account so unfortunately your
            order won&apos;t be visible in your order history.
          </p>
          <p>
            Your personal details have also not been saved so when you place your order next time
            you&apos;ll need to enter this information again. Alternatively you can update your details
            in <MyAccountLink />.
          </p>
          <p>We apologise for any inconvenience caused.</p>
        </>
      );
    }

    case errorCodeConstants.CLIENT_APPS_CREATE_ACCOUNT_DETAILS_NOT_SAVED_ORDER_NOT_LINKED: {
      return (
        <>
          <p>
            We had some technical issues linking your order to your account so unfortunately your
            order won&apos;t be visible in your order history.
          </p>
          <p>Your personal details have also not been saved so when you place your order next time
            you&apos;ll need to enter this information again. Alternatively you can update your details
            in My Account.
          </p>
          <p>We apologise for any inconvenience caused.</p>
        </>
      );
    }

    case errorCodeConstants.SESSION_EXPIRED:
    case errorCodeConstants.ORDER_FORM_EMPTY:
    case errorCodeConstants.ORDER_FORM_NOT_FOUND:
    case errorCodeConstants.CODE_UNAUTHORIZED_TOKEN: {
      return <>Please <ReturnToBasketLink /> and try again. If the problem persists, please try later.</>;
    }

    case errorCodeConstants.INSUFFICIENT_STOCK: {
      return 'Please try again. If the problem persists, please try again later.';
    }

    case errorCodeConstants.INVALID_ADDRESS_DETAILS:
    case errorCodeConstants.POSTCODE_OUT_OF_AREA: {
      return (
        <>
          Please try a different delivery address or{' '}
          <Link
            to={routeConstants.EDIT_BASKET}
          >edit your basket
          </Link>{' '}
          to remove the service/product.
        </>
      );
    }

    case errorCodeConstants.CLIENT_CREATE_ACCOUNT_ORDER_NOT_LINKED: {
      return (
        <>
          <p>
            We had some technical issues linking your order to your account so unfortunately your order
            won&apos;t be visible in your order history.
          </p>
          <p>
            However your order has been successful and all your personal details have been saved and can
            be used for future orders.
          </p>
          <p>We apologise for any inconvenience caused.</p>
        </>
      );
    }

    case errorCodeConstants.PAYMENT_POS_CREDIT_APPLICATION_INCOMPLETE: {
      return (
        <>
          <p>If you have made an application, we have cancelled your payment plan with Creation Consumer Finance Ltd.
          </p>
          <p>Please apply for a payment plan or try another payment method.</p>
        </>
      );
    }

    case errorCodeConstants.GIFT_CARD_SERVICE_UNAVAILABLE:
    case errorCodeConstants.UNKNOWN_GIFT_CARD_SERVICE_RESPONSE_CODE: {
      return (
        <>
          <p>
            You may have entered the gift card details incorrectly or
            there may be a technical issue with the gift card service.
          </p>
          <p>
            If the problem persists try again later or proceed with your
            order and save your gift card for another time.
          </p>
        </>
      );
    }

    case errorCodeConstants.CLIENT_UNKNOWN_TECHNICAL_ERROR:
    case errorCodeConstants.SUBMIT_ORDER_SERVER_INTERNAL_ERROR:
    case 'unknownError': {
      return (
        <>
          Please{' '}
          <UnderlinedButton
            onClick={reload}
          >
            refresh the page
          </UnderlinedButton> and try again.
        </>
      );
    }

    default: {
      return body;
    }
  }
};

export default errorMessageBodyLookup;
